// Full-duplex speech-to-speech over WebRTC (GPT-Live architecture).
//
// Mic and model audio stay open at the same time. semantic_vad on the server
// decides whether a sound is a backchannel or a real barge-in — the client
// must NOT cancel every speech_started (that turns the call into a walkie-talkie).
// Explicit orb tap still hard-interrupts.
//
// The Worker authenticates SDP (`/api/voice/realtime/session?mode=conversation`);
// the API key never leaves the Worker.

import { safeFetch } from './safeFetch.js';

const CONNECT_TIMEOUT_MS = 15_000;

function sdpErrorMessage(status, responseText) {
  try {
    const data = JSON.parse(responseText);
    return data?.error || `OpenAI Realtime ${status}`;
  } catch {
    return responseText.trim() ? responseText.slice(0, 200) : `OpenAI Realtime ${status}`;
  }
}

function withTimeout(promise, ms, message, setTimer = setTimeout, clearTimer = clearTimeout, signal) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimer(timer);
      signal?.removeEventListener('abort', aborted);
    };
    const aborted = () => { cleanup(); reject(new Error('Conversa cancelada')); };
    const timer = setTimer(() => { cleanup(); reject(new Error(message)); }, ms);
    signal?.addEventListener('abort', aborted, { once: true });
    if (signal?.aborted) aborted();
    promise.then(
      value => { cleanup(); resolve(value); },
      error => { cleanup(); reject(error); },
    );
  });
}

function defaultSetTimeout(handler, timeout) {
  return globalThis.setTimeout(handler, timeout);
}
function defaultClearTimeout(id) {
  return globalThis.clearTimeout(id);
}

const CONVERSATION_EVENT_KIND = {
  'input_audio_buffer.speech_started': 'barge_in',
  'input_audio.speech_started': 'barge_in',
  'response.created': 'thinking',
  'output_audio_buffer.started': 'speaking',
  'response.output_audio.delta': 'speaking',
  'response.audio.delta': 'speaking',
  'input_audio_buffer.speech_stopped': 'user_pause',
  'conversation.item.input_audio_transcription.completed': 'transcript',
  'input_audio_transcription.completed': 'transcript',
  'response.done': 'response_done',
  'output_audio_buffer.stopped': 'done',
  'output_audio_buffer.cleared': 'done',
  error: 'error',
  'session.created': 'session',
  'session.updated': 'session',
  'response.function_call_arguments.done': 'tool_call',
};

function conversationEventKind(type) {
  return CONVERSATION_EVENT_KIND[type] || '';
}

export function conversationErrorMessage(event) {
  return (
    event?.error?.message ||
    event?.message ||
    event?.error?.code ||
    'Falha na conversa por voz'
  );
}

export class RealtimeConversation {
  constructor({
    sessionUrl,
    token,
    language = 'pt',
    voice = 'cedar',
    onState,
    onError,
    onFallback,
    onToolCall,
    onMetric = undefined,
    tools = true,
    fetchImpl = safeFetch,
    PeerConnection = globalThis.RTCPeerConnection,
    getUserMedia = (constraints) => navigator.mediaDevices.getUserMedia(constraints),
    AudioCtor = globalThis.Audio,
    setTimer = defaultSetTimeout,
    clearTimer = defaultClearTimeout,
  }) {
    this.sessionUrl = sessionUrl;
    this.token = token;
    this.language = language;
    this.voice = voice;
    this.onState = onState;
    this.onError = onError;
    this.onFallback = onFallback;
    this.onToolCall = onToolCall;
    this.onMetric = onMetric;
    this.tools = tools;
    this.fetchImpl = fetchImpl;
    this.PeerConnection = PeerConnection;
    this.getUserMedia = getUserMedia;
    this.AudioCtor = AudioCtor;
    this.setTimer = setTimer;
    this.clearTimer = clearTimer;

    this.pc = null;
    this.channel = null;
    this.stream = null;
    this.audioEl = null;
    this.closed = false;
    this.replyWatch = null;
    this.sessionType = '';
    this.speaking = false;
    this.abortController = new AbortController();
    this.responseActive = false;
    this.userSpeaking = false;
    this.toolCalls = new Set();
    this.pendingTools = 0;
    this.needsContinuation = false;
    this.continuationPending = false;
    this.turnEpoch = 0;
  }

  async start() {
    if (typeof this.PeerConnection !== 'function') {
      throw new Error('Conversa por voz em tempo real não suportada neste dispositivo');
    }

    try {
      const startedAt = Date.now();
      const stream = await this.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          voiceIsolation: true,
        },
      });
      if (this.closed) {
        stream.getTracks?.().forEach((track) => track.stop());
        throw new Error('Conversa cancelada');
      }
      this.stream = stream;
      await withTimeout(this.connect(), CONNECT_TIMEOUT_MS, 'Tempo esgotado ao conectar voz', this.setTimer, this.clearTimer, this.abortController.signal);
      if (this.closed) throw new Error('Conversa cancelada');
      this.onMetric?.({ name: 'connection_ms', value: Date.now() - startedAt });
      this.onState?.('listening');
    } catch (error) {
      this.close();
      throw error;
    }
  }

  async connect() {
    const track = this.stream.getAudioTracks?.()[0] || this.stream.getTracks?.()[0];
    if (!track) throw new Error('Microfone não encontrado');

    this.pc = new this.PeerConnection();
    if (typeof this.AudioCtor === 'function') {
      this.audioEl = new this.AudioCtor();
      this.audioEl.autoplay = true;
    }
    this.pc.ontrack = (ev) => {
      const remote = ev.streams?.[0];
      if (!remote || !this.audioEl) return;
      this.audioEl.srcObject = remote;
      this.playRemoteAudio();
    };
    this.pc.addTrack(track, this.stream);
    this.channel = this.pc.createDataChannel('oai-events');
    this.channel.addEventListener('message', (message) => this.handleMessage(message));

    const channelOpen = new Promise((resolve, reject) => {
      this.channel.addEventListener('open', resolve, { once: true });
      this.channel.addEventListener(
        'error',
        () => reject(new Error('Canal de conversa falhou ao conectar')),
        { once: true },
      );
    });

    channelOpen.catch(() => {});
    this.channel.addEventListener('close', () => this.fail(new Error('Conexão de voz encerrada')));
    this.pc.addEventListener?.('connectionstatechange', () => this.connectionChanged());
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    const params = new URLSearchParams({
      language: this.language,
      mode: 'conversation',
      voice: this.voice || 'cedar',
      tools: this.tools && this.onToolCall ? '1' : '0',
    });
    const response = await this.fetchImpl(`${this.sessionUrl}?${params}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/sdp',
        ...(this.token ? { Authorization: `Bearer ${this.token}` } : {}),
      },
      body: offer.sdp,
      signal: this.abortController.signal,
    });

    const responseText = await response.text();
    if (!response.ok) throw new Error(sdpErrorMessage(response.status, responseText));

    // CORS exposes X-Apolo-Realtime-Mode (see apolo-api/src/middleware/cors.js).
    const modeHeader = response.headers?.get?.('X-Apolo-Realtime-Mode');
    if (modeHeader === 'conversation') this.sessionType = 'realtime';
    if (modeHeader === 'transcription') this.sessionType = 'transcription';

    if (this.closed) throw new Error('Conversa cancelada');
    await this.pc.setRemoteDescription({ type: 'answer', sdp: responseText });
    await channelOpen;
  }

  playRemoteAudio() {
    this.audioEl?.play?.().catch(() => this.fail(new Error('Não foi possível reproduzir áudio. Tente reconectar.')));
  }

  fail(error) {
    if (this.closed) return;
    this.close();
    this.onError?.(error);
  }

  connectionChanged() {
    this.clearTimer(this.disconnectTimer);
    if (this.pc?.connectionState === 'failed') this.fail(new Error('Conexão de voz perdida'));
    if (this.pc?.connectionState === 'disconnected') {
      this.disconnectTimer = this.setTimer(() => this.fail(new Error('Conexão de voz interrompida')), 5000);
    }
  }

  clearReplyWatch() {
    if (this.replyWatch != null) {
      this.clearTimer(this.replyWatch);
      this.replyWatch = null;
    }
  }

  armFallback() {
    this.clearReplyWatch();
    if (this.closed) return;
    // A live S2S session may reason for a few seconds before the first audio
    // chunk. Falling back here dumps the user into the 15s STT→agent→TTS chain.
    if (this.sessionType === 'realtime') {
      this.replyWatch = this.setTimer(() => this.fail(new Error('Sem resposta de voz. Reconecte para tentar novamente.')), 30_000);
      return;
    }
    if (!this.onFallback) return;
    const waitMs = this.sessionType === 'transcription' ? 800 : 2_500;
    this.replyWatch = this.setTimer(() => {
      this.replyWatch = null;
      this.onFallback?.();
    }, waitMs);
  }

  sendEvent(payload) {
    if (this.closed || this.channel?.readyState !== 'open') return;
    try {
      this.channel.send(JSON.stringify(payload));
    } catch {
      // tearing down
    }
  }

  /** Orb tap: hard-stop the current reply. Do not pause the remote track —
   *  that would tear down the duplex pipe; `output_audio_buffer.clear` is enough. */
  interrupt() {
    this.clearReplyWatch();
    this.turnEpoch += 1;
    this.needsContinuation = false;
    if (this.responseActive) this.sendEvent({ type: 'response.cancel' });
    if (this.speaking) this.sendEvent({ type: 'output_audio_buffer.clear' });
    // WebRTC's server owns playback position and truncation, not Date.now().
    this.speaking = false;
    this.onState?.('listening');
  }

  continueAfterTools() {
    if (!this.needsContinuation || this.pendingTools || this.responseActive ||
        this.speaking || this.userSpeaking || this.continuationPending || this.closed) return;
    this.needsContinuation = false;
    this.continuationPending = true;
    this.sendEvent({ type: 'response.create' });
  }

  async toolOutput(event) {
    const args = event.arguments ? JSON.parse(event.arguments) : {};
    if (!args || typeof args !== 'object' || Array.isArray(args)) throw new Error('Argumentos inválidos');
    const controller = new AbortController();
    const abort = () => controller.abort();
    this.abortController.signal.addEventListener('abort', abort, { once: true });
    try {
      return await withTimeout(
        Promise.resolve().then(() => {
          if (controller.signal.aborted) throw new Error('Conversa cancelada');
          return this.onToolCall(String(event.name || ''), args, { signal: controller.signal });
        }),
        20_000, 'Ferramenta demorou para responder', this.setTimer, this.clearTimer, this.abortController.signal,
      );
    } finally {
      controller.abort();
      this.abortController.signal.removeEventListener('abort', abort);
    }
  }

  async handleToolCall(event) {
    const callId = event.call_id;
    if (this.closed || !this.onToolCall || !callId || this.toolCalls.has(callId)) return;
    // Bound retained IDs without forgetting calls and allowing duplicate effects.
    if (this.toolCalls.size >= 512) return this.fail(new Error('Limite da sessão de voz atingido. Reconecte.'));
    this.toolCalls.add(callId);
    const epoch = this.turnEpoch;
    this.pendingTools += 1;
    let output;
    try {
      if (this.pendingTools > 4) throw new Error('Muitas ferramentas simultâneas');
      output = await this.toolOutput(event);
    } catch (error) {
      output = `Falhou: ${error?.message || error}`;
    } finally {
      this.pendingTools -= 1;
    }
    if (this.closed) return;
    this.sendEvent({ type: 'conversation.item.create', item: {
      type: 'function_call_output', call_id: callId, output: String(output ?? '').slice(0, 6000),
    } });
    if (epoch === this.turnEpoch) this.needsContinuation = true;
    this.continueAfterTools();
  }

  handleMessage(message) {
    if (this.closed) return;
    let event;
    try {
      event = JSON.parse(message?.data);
    } catch {
      return;
    }
    this.dispatchEvent(conversationEventKind(String(event?.type || '')), event);
  }

  dispatchEvent(kind, event) {
    const handlers = {
      session: () => {
        const next = String(event.session?.type || '');
        if (next) this.sessionType = next;
      },
      thinking: () => {
        this.responseActive = true;
        this.continuationPending = false;
        this.armFallback();
        if (!this.speaking) this.onState?.('thinking');
      },
      barge_in: () => {
        this.clearReplyWatch();
        this.userSpeaking = true;
        this.turnEpoch += 1;
        this.needsContinuation = false;
        // With interrupt_response enabled, WebRTC clears/truncates on the server.
        this.speaking = false;
        this.onState?.('listening');
      },
      speaking: () => {
        if (!this.speaking && this.userStoppedAt) {
          this.onMetric?.({ name: 'reply_audio_ms', value: Date.now() - this.userStoppedAt });
          this.userStoppedAt = null;
        }
        this.speaking = true;
        this.clearReplyWatch();
        this.onState?.('speaking');
        this.playRemoteAudio();
      },
      transcript: () => {
        if (this.sessionType !== 'realtime') this.armFallback();
      },
      user_pause: () => {
        this.userSpeaking = false;
        if (/speech_stopped$/.test(event.type)) this.userStoppedAt = Date.now();
        if (!this.speaking) this.onState?.('thinking');
        this.armFallback();
      },
      response_done: () => {
        this.responseActive = false;
        this.clearReplyWatch();
        if (event.response?.status === 'failed') {
          this.fail(new Error(event.response.status_details?.error?.message || 'A resposta de voz falhou'));
          return;
        }
        if (!this.speaking && !this.pendingTools) this.onState?.('listening');
        this.continueAfterTools();
      },
      tool_call: () => {
        void this.handleToolCall(event);
      },
      done: () => {
        this.speaking = false;
        this.onState?.('listening');
        this.continueAfterTools();
      },
      error: () => {
        if (event.error?.code === 'response_cancel_not_active') return;
        this.fail(new Error(conversationErrorMessage(event)));
      },
    };
    handlers[kind]?.();
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    this.abortController.abort();
    this.clearTimer(this.disconnectTimer);
    this.clearReplyWatch();
    try {
      this.audioEl?.pause?.();
      if (this.audioEl) this.audioEl.srcObject = null;
    } catch {
      /* ignore */
    }
    this.audioEl = null;
    this.stream?.getTracks?.().forEach((track) => track.stop());
    try {
      this.channel?.close();
    } catch {
      // already closed
    }
    try {
      this.pc?.close();
    } catch {
      // already closed
    }
    this.channel = null;
    this.pc = null;
    this.stream = null;
  }
}
